import { useMemo } from 'react'

import { theme } from '@hhgtech/hhg-components'
import { Text } from '@hhgtech/hhg-components/mantine'
import { SocialType } from '@hhgtech/hhg-components/types'
import {
  Facebook,
  Instagram,
  Line,
  Linkedin,
  Telegram,
  Tiktok,
  Twitter,
  Whatsapp,
  Zalo,
} from '@hhgtech/icons/other'
import { Box, BoxProps } from '@mantine/core'
import cls from 'classnames'
import { useIntl } from 'react-intl'

import { ZaloSharing } from 'components/atoms/zaloSharing'
import { useCurrentUrl } from 'hooks/useCurrentUrl'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'

import AsideNavView from './view'
import { CopyInput } from '../copyInput'
import { SocialItem } from '../types'
import { getSocialListByLocale } from '../utils'

export type Props = {
  shareUrl?: string
  iconSize?: number
  bgColor?: string
  isRevamp?: boolean
} & BoxProps

export const useSocialListRevamp = (
  socialListOriginal: SocialItem[],
  isRevamp?: boolean,
) => {
  return useMemo(() => {
    const mapRevampIcons: Partial<Record<SocialType, JSX.Element>> = {
      [SocialType.Facebook]: <Facebook />,
      [SocialType.Twitter]: <Twitter />,
      [SocialType.WhatsApp]: <Whatsapp />,
      [SocialType.Line]: <Line />,
      [SocialType.Zalo]: <Zalo />,
      [SocialType.Tiktok]: <Tiktok />,
      [SocialType.Instagram]: <Instagram />,
      [SocialType.LinkedIn]: <Linkedin />,
      [SocialType.Telegram]: <Telegram />,
    }
    return isRevamp
      ? socialListOriginal.map((social) => {
          return {
            ...social,
            icon: mapRevampIcons[social.type] || (
              <Box className="w-6 h-6">{social.icon}</Box>
            ),
          }
        })
      : socialListOriginal
  }, [socialListOriginal, isRevamp])
}

export const AsideNav = ({ isRevamp, ...props }: Props) => {
  const { shareUrl, iconSize = 28 } = props
  const { formatMessage: f } = useIntl()
  const { locale } = useRouterExtend()
  const currentUrl = useCurrentUrl()
  const isMobile = useIsMobile()
  const socialListOriginal = useMemo(
    () =>
      getSocialListByLocale(locale, shareUrl || currentUrl, iconSize, isMobile),
    [locale, shareUrl, currentUrl, iconSize, isMobile],
  )
  const socialList = useSocialListRevamp(socialListOriginal, isRevamp)

  if (isRevamp) {
    return (
      <Box>
        <Text size="n1" className="whitespace-nowrap text-center">
          {f({ id: 'shareArticle.asideNav.share', defaultMessage: 'Share' })}
        </Text>
        {socialList.map((social: SocialItem) => {
          const isZalo = social.type === SocialType.Zalo
          return (
            <Box
              key={social.type}
              component={isZalo ? 'div' : 'a'}
              className="w-10 h-10 p-2 relative cursor-pointer block rounded-lg hover:bg-gray100"
              data-event-category="Social Share"
              data-event-action={social.name}
              data-event-label={currentUrl}
              sx={{
                '&:hover .placeholder': {
                  backgroundColor: theme.colors.gray100,
                },
              }}
              {...(isZalo
                ? {}
                : {
                    target: '_blank',
                    href: social.href,
                  })}
            >
              {isZalo && (
                <>
                  <Box className="placeholder z-[2] bg-white rounded-lg absolute top-0 left-0 right-0 bottom-0" />
                  <ZaloSharing
                    href={social.href}
                    layout={4}
                    className="z-[1] !absolute !w-auto !h-auto top-0 left-0 right-0 bottom-0 !pointer-events-auto"
                  />
                </>
              )}
              <Box
                className={cls('rounded overflow-hidden', {
                  'z-[3] relative': isZalo,
                })}
              >
                {social.icon}
              </Box>
            </Box>
          )
        })}

        <CopyInput shareUrl={shareUrl} type="button" isRevamp />
      </Box>
    )
  }

  return <AsideNavView {...props} socialList={socialList} />
}
